import tripCityList from "../../../data/tripCityList";
import cities from "../../../constants/cities";
import { isAirportPickup } from "./utils";

const DEFAULT_LOCALE = "en-PK";

export const getPlaceLabel = (location) => {
  if (!location?.name) {
    return "";
  }
  if (typeof location.name === "object") {
    return location.name.label || "";
  }
  return location.name;
};

export const getCityNameFromPlace = (location) => {
  const place = location?.name;
  if (!place || typeof place !== "object") {
    return "";
  }

  const terms = place.value?.terms;
  if (terms?.length >= 2) {
    return terms[terms.length - 2].value;
  }

  const secondary = place.value?.structured_formatting?.secondary_text;
  if (secondary) {
    const parts = secondary.split(",").map((part) => part.trim());
    if (parts.length >= 2) {
      return parts[parts.length - 2] || parts[0];
    }
    return parts[0] || "";
  }

  if (place.label) {
    const parts = place.label.split(",").map((part) => part.trim());
    if (parts.length >= 2) {
      return parts[parts.length - 2];
    }
  }

  return "";
};

export const resolveCityIdByName = (cityName) => {
  if (!cityName) {
    return null;
  }

  const normalized = cityName.trim().toLowerCase();
  const match = cities.find(
    (city) => city.Name.toLowerCase() === normalized
  );

  return match?.Id ?? null;
};

export const getAirportCodeFromPlace = (location) => {
  const label = getPlaceLabel(location);
  if (!label) {
    return "";
  }

  const parenMatch = label.match(/\(([A-Z]{3})\)/);
  if (parenMatch) {
    return parenMatch[1];
  }

  if (/schiphol|amsterdam airport/i.test(label)) {
    return "AMS";
  }
  if (/heathrow/i.test(label)) {
    return "LHR";
  }
  if (/gatwick/i.test(label)) {
    return "LGW";
  }
  if (/inverness airport/i.test(label)) {
    return "INV";
  }

  const airportMatch = label.toUpperCase().match(/\b([A-Z]{3})\s+AIRPORT\b/);
  if (airportMatch) {
    return airportMatch[1];
  }

  return "";
};

const buildPoiLocation = (placeField, formValues, cityId) => {
  const poi = placeField?.address;
  const adults = parseInt(formValues.passengerCount, 10) || 1;

  const location = {
    poinm:
      poi?.poinm ||
      (typeof placeField?.name === "string" ? placeField.name : ""),
    poiadr: poi?.poiaddr || poi?.poiadr || "",
    poilat: String(poi?.poilat ?? ""),
    poilng: String(poi?.poilng ?? ""),
    poiref: poi?.poiref || "",
    adult: adults,
    children: 0,
    poicityid: String(cityId ?? poi?.cid ?? ""),
    carPlaceId: poi?.carPlaceId || "",
  };

  if (placeField?.session?.clientId) {
    location.clientId = placeField.session.clientId;
  }
  if (placeField?.session?.traceId) {
    location.traceId = placeField.session.traceId;
  }

  return location;
};

export const buildTransferRatesBody = (formValues) => {
  const airportPickup = isAirportPickup(formValues.tripType);
  const destinationPlace = airportPickup ? formValues.dropoff : formValues.pickup;
  const airportPlace = airportPickup ? formValues.pickup : formValues.dropoff;

  const selectedCity = formValues.cityName
    ? tripCityList.find((city) => city.Name === formValues.cityName)
    : formValues.cityId
      ? tripCityList.find(
          (city) => String(city.Id) === String(formValues.cityId)
        )
      : null;
  const cityName =
    selectedCity?.Name ||
    getCityNameFromPlace(destinationPlace) ||
    getCityNameFromPlace(airportPlace);
  const cityId = selectedCity?.Id ?? resolveCityIdByName(cityName);
  const airportCode = getAirportCodeFromPlace(airportPlace);
  const cityAirportCode = selectedCity?.code || "";

  if (airportPickup) {
    return {
      rideType: "from_airport",
      flightNo: formValues.flightNumber.trim().toUpperCase(),
      departureDate: formValues.date,
      fromAirportCd: airportCode || cityAirportCode,
      location: buildPoiLocation(formValues.dropoff, formValues, cityId),
      currency: formValues.currency,
      locale: DEFAULT_LOCALE,
    };
  }

  return {
    rideType: "to_airport",
    fromAirportCd: cityAirportCode,
    toAirportCd: airportCode || cityAirportCode,
    pickupDateTime: formValues.dateTime.replace("T", " ").slice(0, 16),
    location: buildPoiLocation(formValues.pickup, formValues, cityId),
    currency: formValues.currency,
    locale: DEFAULT_LOCALE,
  };
};

export const validateTransferRatesBody = (body, tripType) => {
  const errors = {};
  const airportPickup = isAirportPickup(tripType);

  if (airportPickup) {
    if (!body.fromAirportCd) {
      errors.pickup =
        "Could not resolve airport code from pickup. Select an airport or include IATA code (e.g. AMS).";
    }

    if (!body.location?.poinm) {
      errors.dropoff =
        "Please select a drop-off location from the search results.";
    } else if (!body.location?.poiref) {
      errors.dropoff =
        "Drop-off location is missing POI reference. Search again and reselect.";
    }

    return errors;
  }

  if (!body.toAirportCd) {
    errors.dropoff =
      "Could not resolve airport code from drop-off. Select an airport or include IATA code (e.g. AMS).";
  }

  if (!body.fromAirportCd) {
    errors.cityId =
      "Could not resolve airport code for the selected city.";
  }

  if (!body.location?.poinm) {
    errors.pickup =
      "Please select a pickup location from the search results.";
  } else if (!body.location?.poiref) {
    errors.pickup =
      "Pickup location is missing POI reference. Search again and reselect.";
  }

  return errors;
};
